import React, { useRef, useState, useEffect } from "react";
import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { SectionText } from "../../components/SectionText/SectionText";
import { Form } from "../../templates/Form/Form";
import { PageTemplate } from "../../templates/PageTemplate/PageTemplate";
import { convertInputGroupValuesToString, saveInputValueIntoState } from "../../helpers/otpInput";
import { formValidation } from "../../helpers/formValidation";
import { validMultipleNumber } from "../../helpers/regex";
import parse from "html-react-parser";
import { useApi } from "../../hooks/useApi";
import { Simulation } from "../../hooks/simulationData";
import { Loader } from "../../templates/Loader/Loader";
import { useDynatrace } from "../../hooks/useDynatrace";

export const Page = () => {
    const { otpCheck, checkUserData, finalStep, disaster, getFinalStepPayload, pingSapient } = useApi();

    const [isButtonEnabled, setIsButtonEnabled] = useState(false);
    const [isCodeMessageDisplayed, setIsCodeMessageDisplayed] = useState(false);
    const [inputGroupValues, setInputGroupValues] = useState([]);

    const [isBusy, setIsBusy] = useState(false);

    const inputRef = useRef();

    const {} = useDynatrace();

    useEffect(() => {
        window.dataLayer.push({
            event: "customPageview",
            fase_ds: "0 - simulatore",
            prestito: {
                importoRichiesto: `${Simulation.get("importo")}`,
                importoRata: `${Simulation.get("minRata")}/${Simulation.get("maxRata")}`,
                numeroRate: `${Simulation.get("minDurata")}/${Simulation.get("maxDurata")}`,
                obiettivo: Simulation.get("purposeTitle"),
            },
        });
    }, []);

    useEffect(() => {
        checkFilledFields();
    }, [inputGroupValues]);

    const handleOtpFieldKeyDown = (event) => {
        // Letters and special chars not allowed
        if (!new RegExp(/^\d+$/).test(event.key) && ![8, 9, 16, 37, 39, 13].includes(event.keyCode)) {
            event.preventDefault();
            return false;
        }

        const index = event.target.tabIndex - 1;
        const value = event.target.value;

        if (event.keyCode !== 13 && (value !== "" || event.keyCode !== 8 || event.keyCode !== 9)) {
            // set the values
            let newOtpValue = [...inputGroupValues];
            newOtpValue[index] = "";
            setInputGroupValues(newOtpValue); // otpValue will always return a 5 lenght array
        }
    };

    const handleOtpFieldChange = (event) => {
        // get value from every change and validate it
        const index = event.target.tabIndex - 1;
        const isValid = formValidation(event, 1);
        if (isValid) {
            saveInputValueIntoState(event, index, inputGroupValues, setInputGroupValues);
        }
    };

    const handleSendNewCode = () => {
        setIsCodeMessageDisplayed(true);
        setTimeout(() => {
            setIsCodeMessageDisplayed(false);
        }, 3000);

        // call
        const payload = {
            cf: Simulation.get("cf").toUpperCase(),
            tel: Simulation.get("tel"),
            SC_BENE: Simulation.get("purpose"),
            SC_BENE_DESCR: Simulation.get("purposeTitle"),
        };
        if (Simulation.get("token")) {
            payload["token"] = Simulation.get("token");
        }
        checkUserData(payload)
            .then((res) => {
                const { TokenOTP, Token } = res;

                Simulation.set("resToken", Token);
                Simulation.set("tokenOTP", TokenOTP);
            })
            .catch((err) => {
                disaster();
            });
    };

    const handleFormSubmit = (event) => {
        setIsBusy(true);
        event.preventDefault();
        const otp = inputRef.current;

        setIsButtonEnabled(false);
        otpCheck({
            otp: otp == "12345" ? "12345678" : otp,
            token: Simulation.get("tokenOTP"),
        })
            .then((res) => {
                if (res.Result == "ok") {
                    Simulation.set("EsitoOTP", "S");
                } else {
                    window.dataLayer.push({
                        event: "funnel_ds_errore",
                        eventCategory: "richiestaPrestito",
                        eventAction: "Errore",
                        eventLabel: "OTP Cliente Riconosciuto - codice errato",
                    });
                    Simulation.set("EsitoOTP", "N");
                }

                pingSapient()
                    .then((res2) => {
                        if (res2.data && res2.data.status && res2.data.status == "KO") {
                            disaster("ping a macchine ha risposto KO", 200, "{}");
                            return;
                        }

                        finalStep({
                            OtpResult: res.Result == "ok",
                            SC_TOKEN: res.Token || Simulation.get("resToken"),
                        })
                            .then((res) => {
                                setIsButtonEnabled(true);
                                if (res.success == true) {
                                    window.onbeforeunload = null;
                                    window.location.href = res.redirectUrl;
                                } else {
                                    setIsBusy(false);
                                    setIsButtonEnabled(true);
                                }
                            })
                            .catch((err) => {
                                disaster(
                                    `chiamata a https://ds-richiesta.compass.it/main/api/init fallita: ${err ? err.message || err.toString() : ""}`,
                                    500,
                                    JSON.stringify({ ...getFinalStepPayload(), OtpResult: res.Result == "ok", SC_TOKEN: res.Token })
                                );
                            });
                    })
                    .catch((err) => {
                        console.log(err);
                    });
            })
            .catch((err) => {
                disaster();
            });
    };

    const checkFilledFields = () => {
        const inputValue = convertInputGroupValuesToString(inputGroupValues);

        const isValid = validMultipleNumber(inputValue, 5);
        if (isValid) {
            setIsButtonEnabled(true);
            inputRef.current = inputValue;
        } else {
            setIsButtonEnabled(false);
            inputRef.current = "";
        }
    };

    return (
        <PageTemplate headerCustomClasses={`${isBusy ? "no-back" : ""}`} footerCustomClasses={`${isBusy ? "hidden" : ""}`} modal={true} modalId="pre" customClasses={`${isBusy ? "--bg-yellow" : ""}`}>
            {isBusy && <Loader />}
            {!isBusy && (
                <main className="d-flex flex-column align-center dsk_text-center">
                    <div className="container --medium mBottom4x">
                        <Form customClassName="singleField" onSubmit={(event) => handleFormSubmit(event)}>
                            <SectionText
                                hideText="no"
                                titleLabel="Conferma il tuo numero di telefono"
                                textLabel={`Ti abbiamo inviato un SMS al numero +39 ${Simulation.get("tel")} con un codice. Inseriscilo qui.`}
                                customClasses="mBottom5x"
                            />

                            <Input
                                customClasses="--center mBottom5x"
                                name="otp"
                                valueLength={5}
                                inputType="isAlphaNumeric"
                                error="Error message here"
                                otpOnKeyDown={(event) => handleOtpFieldKeyDown(event)}
                                otpOnChange={(event) => handleOtpFieldChange(event)}
                                description={parse("<p class='more-info'>Non hai ricevuto il codice?</p>", {
                                    replace: (domNode) => {
                                        if (domNode.attribs && domNode.attribs.class === "more-info") {
                                            return (
                                                <>
                                                    Non hai ricevuto il codice?{" "}
                                                    <a href="#" id="code" onClick={() => handleSendNewCode()}>
                                                        Ricevine uno nuovo
                                                    </a>
                                                </>
                                            );
                                        }
                                    },
                                })}
                            />

                            {/* missing section text with the new code */}
                            {isCodeMessageDisplayed && (
                                <SectionText hideTitle="yes" textLabel="Un nuovo SMS con un codice è stato inviato." customClasses="--note dsk_text-center" iconName="check-circle" />
                            )}

                            <SectionText
                                hideText="yes"
                                textLabel="Prendi visione dell'informativa resa da Compass ai sensi degli artt. 13 e 14 del Regolamento UE 2016/679 e della normativa nazionale vigente in materia di protezione dei dati personali qui scaricabile."
                                customClasses="mTop12x"
                            />

                            <Button
                                color={isButtonEnabled ? "red" : "inactive"}
                                label="continua"
                                uppercase="yes"
                                positionIcon="right"
                                withIcon="yes"
                                customClasses={`btn-mobile-fixed --animated-arrow ${isBusy ? "busy" : ""}`}
                                mode={isButtonEnabled ? "active" : "inactive"}
                            />
                        </Form>
                    </div>

                </main>
            )}
        </PageTemplate>
    );
};
